import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import sortBy from 'lodash-es/sortBy';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { rulesEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { Rule, Rules } from '@/shared/types/rules';
import { EventName } from '@/shared/types/segment';

export const API = Object.freeze({
  listRules: async (deskId: string): Promise<Rules> => callGet(endpoints.rules(deskId)),

  getRule: async (deskId: string, ruleId: string): Promise<Rule> =>
    callGet(endpoints.rule(deskId, ruleId)),

  addRule: async (deskId: string, newRule: Partial<Rule>): Promise<Rule> =>
    callPost(endpoints.rules(deskId), newRule),

  updateRule: async (deskId: string, rule: Partial<Rule>): Promise<Rule> =>
    callPut(endpoints.rule(deskId, rule.rule_id), rule),

  reorderRule: async (deskId: string, ruleId: string, position: number): Promise<Rule> =>
    callPut(endpoints.reorder(deskId, ruleId), { position }),

  deleteRule: async (deskId: string, rule_id): Promise<Rule> =>
    callDelete(endpoints.rule(deskId, rule_id)),
});

export type ReorderProps = { rule_id: string; position: number };

const NO_RULES: Rules = { rules: undefined };

const sortByPosition = (data: Rules): Rules => ({ ...data, rules: sortBy(data.rules, 'position') });

export const onRuleUpdated = (queryClient: QueryClient, rule: Rule, deskId: string) => {
  queryClient.setQueryData<Rule>([endpoints.rule(deskId, rule.rule_id)], (prev: Rule) => ({
    ...prev,
    ...rule,
  }));

  const queryKey = [endpoints.rules(deskId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Rules>(queryKey, (prev: Rules) => ({
      rules: (prev?.rules || []).map((item) =>
        item.rule_id === rule.rule_id ? { ...item, ...rule } : item
      ),
    }));
  }
};
export const onRuleCreated = (queryClient: QueryClient, rule: Rule, deskId: string) => {
  const queryKey = [endpoints.rules(deskId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Rules>(queryKey, (prev) => ({
      rules: [...(prev?.rules || []).filter((item) => item.rule_id !== rule.rule_id), rule],
    }));
  }
};

const onRuleReorder = (queryClient: QueryClient, deskId: string, rule: Rule, rules: Rules) => {
  const prevPosition = rules?.rules?.find((r) => r.rule_id === rule.rule_id)?.position;
  const newPosition = rule.position;

  if (prevPosition === undefined || prevPosition === newPosition) {
    return;
  }

  queryClient.setQueryData<Rule>([endpoints.rule(deskId, rule.rule_id)], (prev: Rule) => ({
    ...prev,
    ...rule,
  }));

  let start: number;
  let end: number;
  let step: number;

  if (prevPosition < newPosition) {
    start = prevPosition;
    end = newPosition;
    step = -1;
  } else {
    start = newPosition;
    end = prevPosition;
    step = 1;
  }

  rules?.rules?.forEach((r) => {
    queryClient.setQueryData<Rule>([endpoints.rule(deskId, r.rule_id)], (prev: Rule) => {
      if (prev?.rule_id === rule.rule_id) {
        return prev;
      }
      if (prev?.position >= start && prev?.position <= end) {
        return { ...prev, position: prev.position + step };
      }
      return prev;
    });
  });

  queryClient.setQueryData<Rules>([endpoints.rules(deskId)], (prev: Rules) => ({
    rules: (prev?.rules || []).map((item) => {
      if (item.position === prevPosition) {
        return rule;
      }
      if (item.position >= start && item.position <= end) {
        return { ...item, position: item.position + step };
      }
      return item;
    }),
  }));
};

export const onRuleRemoved = (queryClient: QueryClient, deskId: string, rule_id: string) => {
  const queryKey = [endpoints.rules(deskId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Rules>(queryKey, (prev: Rules) => {
      const prevPosition = (prev?.rules || []).find((rule) => rule.rule_id === rule_id)?.position;
      return {
        rules: (prev?.rules || [])
          .filter((rule) => rule.rule_id !== rule_id)
          .map((rule) =>
            rule.position < prevPosition ? rule : { ...rule, position: rule.position - 1 }
          ),
      };
    });
    const rules = queryClient.getQueryData<Rules>(queryKey);
    rules.rules.forEach((rule) =>
      queryClient.setQueryData<Rule>([endpoints.rule(deskId, rule.rule_id)], (prev: Rule) =>
        prev ? { ...prev, position: rule.position } : prev
      )
    );
  }
  queryClient.removeQueries({ queryKey: [endpoints.rule(deskId, rule_id)] });
};

/** Create, update, save and delete, without subscribing to the rules list. */
export const useRuleMutations = (deskId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const onUpdateSuccess = (resp: Rule) => {
    onRuleUpdated(queryClient, resp, deskId);
    toast.success(t('rules.rule_updated', { 0: resp.name }));
  };

  const { mutate: updateRule, status: updateStatus } = useMutation<Rule, Error, Partial<Rule>>({
    mutationFn: (rule) => API.updateRule(deskId, rule),
    onSuccess: onUpdateSuccess,
    meta: { errorToast: true },
  });

  // Save from the rule panel. The menu toggle uses `updateRule`, so it does not send Rule Updated.
  const { mutate: saveRule, status: saveStatus } = useMutation<Rule, Error, Partial<Rule>>({
    meta: {
      segment: {
        success: EventName.RuleUpdated,
        action: 'rule_update',
        successProps: (rule: Rule) => ({ rule_id: rule.rule_id }),
      },
      errorToast: true,
    },
    mutationFn: (rule) => API.updateRule(deskId, rule),
    onSuccess: onUpdateSuccess,
  });

  const { mutate: addRule, status: createStatus } = useMutation<Rule, Error, Partial<Rule>>({
    meta: {
      segment: {
        success: EventName.RuleCreated,
        action: 'rule_create',
        successProps: (rule: Rule) => ({ rule_id: rule.rule_id }),
      },
      errorToast: true,
    },
    mutationFn: (newRule) => API.addRule(deskId, newRule),
    onSuccess: (resp) => {
      onRuleCreated(queryClient, resp, deskId);
      toast.success(t('rules.rule_created_successfully'));
    },
  });

  const { mutate: deleteRule } = useMutation<Rule, Error, string>({
    mutationFn: (id) => API.deleteRule(deskId, id),
    onSuccess: (resp) => {
      onRuleRemoved(queryClient, deskId, resp.rule_id);
      toast.success(t('rules.rule_deleted'));
    },
    meta: { errorToast: true },
  });

  return { updateRule, updateStatus, saveRule, saveStatus, addRule, createStatus, deleteRule };
};

export const useRules = (deskId: string, ruleId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const mutations = useRuleMutations(deskId);

  const { data: rules, status: listStatus } = useQuery<Rules, Error>({
    queryKey: [endpoints.rules(deskId)],
    queryFn: () => API.listRules(deskId),
    select: sortByPosition,
    enabled: !!deskId,
  });

  const { data: rule, status: getStatus } = useQuery<Rule, Error>({
    queryKey: [endpoints.rule(deskId, ruleId)],
    queryFn: () => API.getRule(deskId, ruleId),
    enabled: !!ruleId && !!deskId,
  });

  const { mutate: reorderRule, status: reorderStatus } = useMutation<Rule, Error, ReorderProps>({
    mutationFn: (params) => API.reorderRule(deskId, params.rule_id, params.position),
    onSuccess: (resp) => {
      onRuleReorder(queryClient, deskId, resp, rules);
      toast.success(t('rules.rule_reordered'));
    },
    meta: { errorToast: true },
  });

  return {
    rules: rules ?? NO_RULES,
    rule,
    getStatus,
    listStatus,
    ...mutations,
    reorderRule,
    reorderStatus,
  };
};
